import { useEffect, useState } from "react";
import { FormControlLabel, Checkbox, IconButton, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { green } from "@mui/material/colors";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import ModalFinanciamiento from "./ModalFinanciamiento";
import ComoTeEnteraste from "../ComoTeEnteraste";
import "./ValorVehiculo.css";
import Swal from "sweetalert2";
import isADevice from "../../utils/isADevice";

const PREFIX = "ValorVehiculo";

const classes = {
  root: `${PREFIX}-root`,
  checked: `${PREFIX}-checked`,
  disabled: `${PREFIX}-disabled`,
};

// TODO jss-to-styled codemod: The Fragment root was replaced by div. Change the tag if needed.
const Root = styled("div")({
  [`& .${classes.root}`]: {
    color: green[400],
    "&$checked": {
      color: green[600],
    },
    "&$disabled": {
      color: green[100], // Aquí defines el color del borde cuando está deshabilitado
    },
  },
  [`& .${classes.checked}`]: {},
});

const GreenCheckbox = (props) => <Checkbox color="default" {...props} />;

const FEATURE_DESCRIPTIONS = {
  "Es salvamento":
    " Vehículos recuperados de la pérdida total de una aseguradora.",
  Legalizado:
    "Vehículos fronterizos usados, originarios de otro país que ingresan a México.",
  "Cuenta con financiamiento":
    "El vehículo se adquirió a crédito y actualmente dicho crédito sigue vigente",
  "No tiene placas": "Placas en trámite o vehículo con permiso de circulación",
  "Soy conductor de plataforma":
    "El vehículo es utilizado (aunque sea de manera ocasional) para plataformas como Uber, Didi, Cabify, etc. Ya sea para reparto o transporte de personas.",
  "Está blindado": false,
};

function getVehicleFeatures(vehicleType) {
  switch (vehicleType) {
    case "auto":
    case "moto":
      return [
        "Es salvamento",
        "Legalizado",
        "Cuenta con financiamiento",
        "No tiene placas",
        "Está blindado",
        "Soy conductor de plataforma",
        "Confirmo que mi vehículo no tiene ninguna de las características anteriores",
      ];
    case "laptop":
    case "tablet":
    case "smartphone":
    case "devices":
      return ["Es restaurado", "Lo acabo de comprar"];
    case "bike":
    case "eBike":
      return [
        "Es salvamento",
        "Cuenta con financiamiento",
        "Soy conductor de plataforma",
        "Confirmo que mi vehículo no tiene ninguna de las características anteriores",
      ];
    case "scooter":
    case "otro":
      return [
        "Es salvamento",
        "Cuenta con financiamiento",
        "Confirmo que mi vehículo no tiene ninguna de las características anteriores",
      ];

    default:
      console.warn(`${vehicleType} is not a valid type`);
      return []; // Return empty array instead of undefined
  }
}

const VEHICLE_PLATFORMS = [
  "Didi",
  "Uber",
  "Beat",
  "Cabify",
  "Rappi",
  "Uber Eats",
  "Otro",
];

const ValorVehiculo = ({
  vehicleData,
  setVehicleData,
  setHowFindOutClupp,
  howFindOutClupp,
  isAgentLogged,
  featuresSelectRef,
  tipoSelected,
}) => {
  const [openFinanciamiento, setOpenFinanciamiento] = useState(false);
  const [disableFeatures, setDisabledFeatures] = useState([]);
  const vehicleFeatures = getVehicleFeatures(tipoSelected);

  useEffect(() => {
    const featuresArray = getVehicleFeatures(vehicleData.type);
    const features = featuresArray.length > 0 ? featuresArray.slice(0, -1) : [];

    if (vehicleData.features.length === 0) {
      setDisabledFeatures([]);
    } else if (
      vehicleData.features.includes(
        "Confirmo que mi vehículo no tiene ninguna de las características anteriores"
      )
    ) {
      setDisabledFeatures(features);
    } else {
      setDisabledFeatures([
        "Confirmo que mi vehículo no tiene ninguna de las características anteriores",
      ]);
    }
  }, [vehicleData.features, vehicleData.type]);

  let queryString = window.location.search;
  let urlParams = new URLSearchParams(queryString);
  let medium = urlParams.get("utm_medium");

  function handleCloseFinanciamiento() {
    setOpenFinanciamiento(false);
    const checked = false;
    const feature = "Cuenta con financiamiento";

    setVehicleData((prev) => {
      const updatedFeatures = checked
        ? [...(prev.features || []), feature]
        : prev.features.filter((item) => item !== feature);
      return {
        ...prev,
        features: updatedFeatures,
      };
    });
  }

  const isPlatformDriver =
    vehicleData.features &&
    vehicleData.features.includes("Soy conductor de plataforma");

  function confirmFinanciamiento(months, name) {
    if (!months || !name.trim()) {
      Swal.fire({
        icon: "warning",
        text: "Debes ingresar el nombre de la financiera y el número de meses",
      });
      return;
    }
    setOpenFinanciamiento(false);
    setVehicleData((prev) => {
      return {
        ...prev,
        financingMonths: months,
        financialName: name.trim(),
      };
    });
  }

  function handleOnFeatureChange(e) {
    const checked = e.target.checked;
    const feature = e.target.value;

    if (
      e.target.value === "Cuenta con financiamiento" &&
      e.target.checked === true
    ) {
      setOpenFinanciamiento(true);
    }

    // Disable the "platform driver" option
    if (e.target.value === "Soy conductor de plataforma") {
      Swal.fire(
        "Atención",
        "Por el momento no estamos asegurando conductores de plataforma.",
        "warning"
      );
      localStorage.removeItem("platformBoolean");
      return;
    }

    if (checked && feature === "Es salvamento") {
      Swal.fire(
        "¡Atención!",
        '<p style="text-align:left">Es necesario que cuentes al menos con una copia de la factura original y de la refactura del vehículo.<br/><br/>En las coberturas de daños materiales, adicional al deducible, tendrás que pagar el 30% de los daños y en caso de que el vehículo se declare como Pérdida total o te lo roben, el monto máximo a indemnizar será el valor del vehículo en su refactura con un descuento del 10% anual.</p>',
        "info"
      );
    }

    if (checked && feature === "Legalizado") {
      Swal.fire(
        "¡Atención!",
        `<p style="text-align:left">Considera qué sólo podemos asegurar vehículos legalizados que:<br/><br/>- Esten en buenas condiciones y tengan menos de 10 años de antigüedad<br/>- Tengan placas y tarjeta de circulación de México (o máximo 10 días de tolerancia).<br/>- Cuenten con el título de propiedad original del país de origen. Que el título de propiedad no sea tipo “Salvaged”, “Reconditioned”, “Non-repairable”, “Reconstructed”, “Homemade” o similares.<br/><br/>Adicionalmente, los vehículos legalizados tienen que pagar 30% de los daños en caso de accidente (adicional al deducible) y en caso de Robo o Pérdida total lo máximo a indemnizar es el valor de su refactura o pedimento con una depreciación del 10% anual.</p>`,
        "info"
      );
    }

    setVehicleData((prev) => {
      const updatedFeatures = checked
        ? [...(prev.features || []), feature]
        : prev.features.filter((item) => item !== feature);

      const isPlatformDriver = updatedFeatures.includes(
        "Soy conductor de plataforma"
      );

      if (
        e.target.value === "Cuenta con financiamiento" &&
        e.target.checked === false
      ) {
        delete prev.financingMonths;
        delete prev.financialName;
        Swal.fire({
          text: "Si tu vehículo es financiado y por lo tanto no cuentas con la factura original, el seguro no podrá cubrir los casos de pérdida total ni robo, ya que se requiere la factura para ambas.",
          confirmButtonText: "Lo entiendo y estoy de acuerdo.",
        });
      }
      return {
        ...prev,
        features: updatedFeatures,
        platforms: isPlatformDriver ? prev.platforms : [],
      };
    });
  }

  function handleOnPlatformChange(e) {
    const checked = e.target.checked;
    const platform = e.target.value;

    setVehicleData((prev) => {
      const updatedPlatforms = checked
        ? [...prev.platforms, platform]
        : prev.platforms.filter((item) => item !== platform);

      console.log(updatedPlatforms);
      return {
        ...prev,
        platforms: updatedPlatforms,
      };
    });
  }

  function openDialogFeature(description) {
    Swal.fire({
      icon: "info",
      text: description,
    });
  }

  return (
    <Root>
      <ModalFinanciamiento
        open={openFinanciamiento}
        onClose={handleCloseFinanciamiento}
        confirmFinanciamiento={confirmFinanciamiento}
      />
      <div className="container-valor">
        <p>
          {`Selecciona si tu ${
            isADevice(vehicleData.type) ? "dispositivo" : "vehículo"
          } tiene alguna de las siguientes
          características:`}
        </p>
        <div className="checkbox-grid" id="product-features">
          {vehicleFeatures &&
            vehicleFeatures.map((feature) => (
              <Box
                key={feature}
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: "5px",
                }}
              >
                <FormControlLabel
                  ref={featuresSelectRef}
                  style={{
                    padding: 0,
                    marginBottom: "15px",
                    textAlign: "left",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                  labelPlacement="end"
                  id={feature}
                  checked={vehicleData?.features.includes(feature)}
                  disabled={disableFeatures.includes(feature)}
                  control={
                    <GreenCheckbox
                      onChange={handleOnFeatureChange}
                      name={feature}
                      sx={{
                        padding: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                      classes={{
                        root: disableFeatures.includes(feature)
                          ? classes.disabled
                          : classes.root,
                        checked: classes.checked,
                      }}
                    />
                  }
                  label={
                    feature.includes("Confirmo") ? <b>{feature}</b> : feature
                  }
                  value={feature}
                />
                {FEATURE_DESCRIPTIONS[feature] ? (
                  <IconButton
                    sx={{
                      width: "2px",
                      height: "0px",
                      padding: 0,
                      margin: "0px 20px 15px 0px",
                    }}
                    onClick={() =>
                      openDialogFeature(FEATURE_DESCRIPTIONS[feature])
                    }
                  >
                    <QuestionMarkIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <Box sx={{ width: "24px", height: "24px" }} />
                )}
              </Box>
            ))}
        </div>

        {isPlatformDriver && (
          <>
            <p>
              Selecciona las plataformas en las que trabajas con tu vehículo:
            </p>

            <div className="checkbox-grid">
              {VEHICLE_PLATFORMS.map((platform) => (
                <FormControlLabel
                  key={platform}
                  style={{
                    margin: 0,
                  }}
                  id={platform}
                  checked={
                    vehicleData.platforms &&
                    vehicleData.platforms.length &&
                    vehicleData.platforms.includes(platform)
                  }
                  control={
                    <GreenCheckbox
                      onChange={handleOnPlatformChange}
                      name={platform}
                      classes={{
                        root: classes.root,
                        checked: classes.checked,
                        disabled: classes.disabled,
                      }}
                    />
                  }
                  label={platform}
                  value={platform}
                />
              ))}
            </div>
          </>
        )}

        {!isAgentLogged && (
          <ComoTeEnteraste
            setHowFindOutClupp={setHowFindOutClupp}
            howFindOutClupp={howFindOutClupp}
          />
        )}
      </div>
    </Root>
  );
};

export default ValorVehiculo;
